/**
 * Interview zod schemas —— 前後端共用。
 * 欄位定義對齊 docs/02-資料字典.md §3.2。
 */
import { z } from 'zod';
import { ORDER_SHIPMENT_STATUSES } from './shipment.schema';
import { AttachmentRotationSchema } from './attachment-rotation.schema';

export const InterviewAttachmentSchema = z.object({
  id: z.number().int().positive(),
  uuid: z.string().uuid(),
  originalName: z.string().max(255),
  storedName: z.string().max(255),
  mimeType: z.string().max(100),
  sizeBytes: z.number().int().nonnegative(),
  storageDriver: z.enum(['local', 's3', 'minio']),
  /** 放大檢視的旋轉角度(REQ-20260909004)。0 = 沒轉過;檔案本身與 EXIF 不受影響 */
  rotation: AttachmentRotationSchema,
  createdAt: z.string().datetime(),
  uploadedBy: z
    .object({ id: z.number().int().positive(), displayName: z.string() })
    .nullable(),
});

export type InterviewAttachment = z.infer<typeof InterviewAttachmentSchema>;

/** Step 1: 前端呼叫 api 要 presigned PUT URL 時的 body。 */
export const PresignUploadSchema = z.object({
  originalName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  sizeBytes: z.number().int().positive(),
});
export type PresignUploadInput = z.infer<typeof PresignUploadSchema>;

export const PresignUploadResponseSchema = z.object({
  uploadUrl: z.string().url(),
  storedName: z.string(),
  storagePath: z.string(),
  expiresInSec: z.number().int().positive(),
});
export type PresignUploadResponse = z.infer<typeof PresignUploadResponseSchema>;

/** Step 2: PUT 完成後呼叫 api 建 DB row。 */
export const CreateAttachmentSchema = PresignUploadSchema.extend({
  storedName: z.string().min(1).max(255),
  storagePath: z.string().min(1).max(1024),
});
export type CreateAttachmentInput = z.infer<typeof CreateAttachmentSchema>;

/** 建立訪談紀錄（POST /customers/:cid/interviews body）。 */
export const CreateInterviewSchema = z.object({
  interviewTypeId: z.number().int().positive().optional().nullable(),
  contact: z.string().trim().max(50).optional().nullable(),
  notes: z.string().max(50000).optional().nullable(),
  visitedAt: z.string().datetime().optional().nullable(),
  /** REQ:每日 AI 追蹤提醒開關;true = 每日推播到 Telegram 直到關閉 */
  notifyDaily: z.boolean().optional(),
  /**
   * REQ-20260810002:業務私帳標籤。使用者手動勾選,或在建應收單時選了「個人」
   * 由後端自動設為 true。沒帶就沿用 DB 預設(建立 = false;更新 = 不動)。
   */
  isPrivateAccount: z.boolean().optional(),
});

export type CreateInterviewInput = z.infer<typeof CreateInterviewSchema>;

export const UpdateInterviewSchema = CreateInterviewSchema.partial();
export type UpdateInterviewInput = z.infer<typeof UpdateInterviewSchema>;

/** UI 展示用 nested 欄位：interviewType + createdBy。 */
export const InterviewTypeBriefSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
});

export const InterviewCreatorBriefSchema = z.object({
  id: z.number().int().positive(),
  displayName: z.string(),
});

export const InterviewResponseSchema = z.object({
  id: z.number().int().positive(),
  customerId: z.number().int().positive(),
  interviewTypeId: z.number().int().nullable(),
  interviewType: InterviewTypeBriefSchema.nullable(),
  contact: z.string().nullable(),
  notes: z.string().nullable(),
  visitedAt: z.string().datetime().nullable(),
  notifyDaily: z.boolean().default(false),
  /** REQ-20260809003:此訪談是否已建立(未作廢的)應收單,決定「新增應收」按鈕的紅/黑燈號 */
  hasReceivable: z.boolean().default(false),
  /**
   * REQ-20260814004:此訪談已建立的(未作廢)應收單單號,訪談紀錄上直接顯示成可點徽章。
   * 判準與 `hasReceivable` 完全相同(排除 VOIDED,見 REQ-20260809003),
   * 由後端即時查關聯算出來,不是 `interviews` 的欄位 —— 單被作廢 / 刪除 / 改掛
   * 別筆訪談,下次查詢自然就不一樣。只帶 id + orderNo,金額與狀態不外洩。
   */
  receivableOrders: z
    .array(
      z.object({
        id: z.number().int().positive(),
        orderNo: z.string(),
        /**
         * REQ-20260815005:這張單的貨款是否已全額收訖。
         * 判準 = 未結餘額(總額 - 已沖銷 - 折讓)<= 0,溢收也算收完;
         * 已沖銷金額是後端即時 groupBy `ar_settlement_lines` 算的,同樣不落地成欄位。
         * 與 `hasReceivable` 是兩個問題:那個回答「有沒有建單」,這個回答「收完了沒」。
         */
        settled: z.boolean(),
        /**
         * REQ-20260815008:這張單的商品出貨到哪了(未出貨 / 部分出貨 / 已出貨)。
         * 與 `settled` 完全平行的第三個問題 —— 那個回答「錢收完了沒」,
         * 這個回答「貨出完了沒」;同樣是後端即時聚合(`shipment_lines`)算的,
         * 不落地成欄位。訂單沒有品項數量可出時算 FULL(見 shipment.schema.ts)。
         */
        shipmentStatus: z.enum(ORDER_SHIPMENT_STATUSES).default('NONE'),
        /**
         * REQ-20260824008:這張單最近一次出貨的日期。
         * 與 `settled` / `shipmentStatus` 同一個模式 —— 後端即時查 `shipments`
         * 取 `max(shippedAt)` 算的,不落地成 `interviews` 的欄位;
         * 出貨紀錄補登 / 刪掉,下次查詢自然就跟著變。
         * 還沒出過貨(通常是 `shipmentStatus=NONE`)時是 null。
         */
        lastShippedAt: z.string().datetime().nullable().default(null),
        /**
         * REQ-20260929003:這張單是不是還在草稿。
         * 草稿沒有出貨紀錄,`shipmentStatus` 會算成 NONE,但登記出貨只放行 POSTED
         * (REQ-20260815008)—— 不分開的話訪談上亮「待出貨」,點進去卻不能填。
         * 「作廢並複製新單」一律產生草稿(REQ-20260821001),所以改單後最常撞到。
         * 後端只回這個布林,不回原始 status 字串。
         */
        isDraft: z.boolean().default(false),
      }),
    )
    .default([]),
  /** REQ-20260810002:業務私帳標籤,時間軸上顯示「👤 私帳」徽章 */
  isPrivateAccount: z.boolean().default(false),
  createdById: z.number().int().positive(),
  createdBy: InterviewCreatorBriefSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  attachments: z.array(InterviewAttachmentSchema).default([]),
});

export type InterviewResponse = z.infer<typeof InterviewResponseSchema>;

/** 訪談類別下拉用。 */
export const InterviewTypeResponseSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
});
export type InterviewTypeResponse = z.infer<typeof InterviewTypeResponseSchema>;

/** 列表查詢參數。 */
export const InterviewListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
  /** REQ-20260421009 §2:訪談類別篩選。 */
  interviewTypeId: z.coerce.number().int().positive().optional(),
  /** REQ-20260803003:關鍵字全文搜尋(比對 notes / contact)。 */
  q: z.string().trim().min(1).max(100).optional(),
});
export type InterviewListQuery = z.infer<typeof InterviewListQuerySchema>;

/**
 * 客戶層級附件列表(圖片牆用,REQ-20260421007)。
 * 目前只支援 `kind=image`,之後擴到 PDF / 全部時延用同一 schema。
 */
export const CustomerAttachmentsQuerySchema = z.object({
  kind: z.enum(['image']).default('image'),
});
export type CustomerAttachmentsQuery = z.infer<typeof CustomerAttachmentsQuerySchema>;

export const CustomerImageAttachmentSchema = z.object({
  uuid: z.string().uuid(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /** 放大檢視的旋轉角度(REQ-20260909004);圖片牆的縮圖也照這個角度顯示 */
  rotation: AttachmentRotationSchema,
  interviewId: z.number().int().positive(),
  interviewCreatedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type CustomerImageAttachment = z.infer<typeof CustomerImageAttachmentSchema>;

export const CustomerAttachmentsResponseSchema = z.object({
  data: z.array(CustomerImageAttachmentSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
  }),
});
export type CustomerAttachmentsResponse = z.infer<
  typeof CustomerAttachmentsResponseSchema
>;
